import type { CSSProperties } from "styled-components";
import { css, styled } from "styled-components";

import { triggerLogin } from "authentication/actions";
import { UIIconButton } from "common/components";
import { breakpoints } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { useWeforumUrl } from "common/utilities/useWeforumUrl";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { StyledLandingButton } from "domains/landing/components/StyledLandingButton";
import { FooterListItem } from "domains/landing/footer/FooterListItem";
import {
  getShouldShowBecomeMemberMarketing,
  getShouldShowBecomeProMemberMarketing,
} from "entities/permissions/selectors";
import { useGoToMembership } from "entities/user/utils";

const StyledContainer = styled.div`
  margin-top: 6rem;
  padding-top: 4rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.fillPrimary};
  padding-bottom: 4rem;
`;

const StyledInnerContainer = styled.div`
  max-width: 1600px;
  display: flex;
  flex-direction: column-reverse;
  margin: 0 2rem;

  @media ${breakpoints.large} {
    margin: 0 4rem;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const StyledColumn = styled.div<{ alignment?: CSSProperties["textAlign"] }>`
  margin-right: 4rem;
  max-width: 400px;
  margin-bottom: 4rem;
  display: flex;
  flex-direction: column;

  &:last-child {
    margin-right: 0;
  }

  @media ${breakpoints.large} {
    margin-bottom: 0;

    ${({ alignment }) =>
      alignment === "right" &&
      css`
        margin-right: 0;
        margin-left: 4rem;
      `}

    ${({ alignment }) =>
      alignment === "center" &&
      css`
        margin-right: 0;
      `}
  }
`;

const StyledTitle = styled.h3`
  text-transform: uppercase;
  font-size: 1.1rem;
  margin-top: 0;
`;

const StyledList = styled.div`
  padding: 0;
`;

const StyledMainTitle = styled.h1`
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: normal;
`;

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const StyledUpgradeButton = styled(StyledLandingButton)`
  max-width: 250px;
`;

const links = [
  { localisedKey: "app.navigation.about_link", to: "/about" },
  { localisedKey: "app.briefings", to: "/briefings" },
  {
    localisedKey: "app.settings.feedback",
    href: "mailto:intelligence@weforum.org",
  },
  { localisedKey: "app.settings.terms_of_use", to: "/terms-and-conditions" },
  { localisedKey: "app.registration.privacy_policy", to: "/privacy-policy" },
  { localisedKey: "app.settings.cookie_notice", to: "/cookie-notice" },
  {
    localisedKey: "app.widget.embed_maps_in_your_site",
    href: "https://widget.intelligence.weforum.org/",
  },
] as const;

const socialMedia = [
  { label: "X", href: "https://x.com/WEF_Intel" },
  { label: "Instagram", href: "https://www.instagram.com/worldeconomicforum/" },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/world-economic-forum/",
  },
];

export const Footer = () => {
  const dispatch = useDispatch();
  const goToMembership = useGoToMembership();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const showMemberUpgrade = useSelector(getShouldShowBecomeMemberMarketing);
  const showProMemberUpgrade = useSelector(getShouldShowBecomeProMemberMarketing);
  const track = useTrack();
  const translate = useTranslate();
  const weforumUrl = useWeforumUrl();

  const onDigitalMembersClick = () => {
    track({ name: showMemberUpgrade ? "Upgrade" : "Upgrade - Pro", page: "Footer" });
    goToMembership();
  };

  const showUpgradeColumn = !isAuthenticated || showMemberUpgrade || showProMemberUpgrade;

  return (
    <StyledContainer>
      <StyledInnerContainer>
        <StyledColumn>
          <StyledTitle>Strategic Intelligence</StyledTitle>
          <StyledList>
            {links.map(({ localisedKey, ...rest }) => (
              <FooterListItem {...rest} key={localisedKey} label={translate(localisedKey)} />
            ))}
          </StyledList>
        </StyledColumn>
        <StyledColumn alignment={showUpgradeColumn ? "center" : "right"}>
          <StyledTitle>{translate("app.social_media.follow_us")}</StyledTitle>
          <StyledList>
            {socialMedia.map((item, index) => (
              <FooterListItem key={index} {...item} />
            ))}
          </StyledList>
        </StyledColumn>
        {showUpgradeColumn ? (
          <StyledColumn alignment="right">
            <StyledMainTitle>{translate("app.upgrade_banner.title")}</StyledMainTitle>
            {isAuthenticated && (
              <StyledUpgradeButton onClick={onDigitalMembersClick}>
                {translate(showMemberUpgrade ? "app.join_forum_community.title_short" : "app.join_forum_community.pro")}
              </StyledUpgradeButton>
            )}
            {!isAuthenticated && (
              <StyledButtonsContainer>
                <UIIconButton variant="outlined" onClick={() => dispatch(triggerLogin())} icon="rightArrow">
                  {translate("app.user.sign_in")}
                </UIIconButton>
                <UIIconButton as="a" href={`${weforumUrl}/join-us`} icon="rightArrow" variant="filled">
                  {translate("app.registration.join_us")}
                </UIIconButton>
              </StyledButtonsContainer>
            )}
          </StyledColumn>
        ) : (
          <div style={{ width: "33%" }}></div>
        )}
      </StyledInnerContainer>
    </StyledContainer>
  );
};
